import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bookmark, BookmarkCheck, BookOpenText, CheckCircle2, Clock3, ExternalLink,
  ImageOff, Newspaper, RefreshCw, Search, Sparkles, Wifi, X
} from "lucide-react";
import { api, ApiError } from "../api";
import { Button, Card, Empty, Toast } from "../components/ui";
import { useAssistantPageContext } from "../assistant-page-context";

type ContentTopic = "科技与 AI" | "国际" | "旅行" | "滑雪体育" | "英语学习";
type VisibleTopic = "推荐" | ContentTopic | "收藏";
type NewsItem = {
  id: string;
  savedId?: string;
  title: string;
  url: string;
  source: string;
  author: string;
  summary: string;
  publishedAt: string | null;
  topic: ContentTopic | "收藏";
  imageUrl: string | null;
  score: number;
};
type SourceStatus = {
  id: string;
  name: string;
  topic: ContentTopic;
  state: "ok" | "error" | "backoff";
  itemCount: number;
  lastSuccessAt: string | null;
  message?: string;
};
type NewsFeed = {
  topic: Exclude<VisibleTopic, "收藏">;
  items: NewsItem[];
  categories: Array<{ key: VisibleTopic; label: string; count: number }>;
  updatedAt: string | null;
  stale: boolean;
  refreshing: boolean;
  partial: boolean;
  sources: SourceStatus[];
  nextCursor: string | null;
};
type Briefing = {
  headline: string;
  overview: string;
  sections: Array<{ title: string; points: Array<{ text: string; sources: Array<{ id: string; title: string; url: string }> }> }>;
  englishCorner: { term: string; meaning: string; example: string };
  reflectionQuestion: string;
  disclaimer: string;
  generatedAt: string;
  briefingDate: string;
};

const defaultCategories: Array<{ key: VisibleTopic; label: string; count: number }> = [
  { key: "推荐", label: "推荐", count: 0 },
  { key: "科技与 AI", label: "科技与 AI", count: 0 },
  { key: "国际", label: "国际", count: 0 },
  { key: "旅行", label: "旅行", count: 0 },
  { key: "滑雪体育", label: "滑雪体育", count: 0 },
  { key: "英语学习", label: "英语学习", count: 0 },
  { key: "收藏", label: "收藏", count: 0 }
];

function errorMessage(error: unknown) {
  return error instanceof ApiError || error instanceof Error ? error.message : "操作没有完成，请稍后再试";
}

function relativeTime(value: string | null) {
  if (!value) return "时间待确认";
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "时间待确认";
  const minutes = Math.max(0, Math.round((Date.now() - time) / 60_000));
  if (minutes < 60) return minutes < 2 ? "刚刚" : `${minutes} 分钟前`;
  if (minutes < 1_440) return `${Math.floor(minutes / 60)} 小时前`;
  if (minutes < 10_080) return `${Math.floor(minutes / 1_440)} 天前`;
  return new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric" }).format(new Date(time));
}

function uniqueItems(items: NewsItem[]) {
  return [...new Map(items.map((item) => [item.id, item])).values()];
}

export function NewsPage() {
  const qc = useQueryClient();
  const { setPageContext } = useAssistantPageContext();
  const [topic, setTopic] = useState<VisibleTopic>("推荐");
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState<{ message: string; type?: "ok" | "error" } | null>(null);
  const [showBriefing, setShowBriefing] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  useEffect(() => { setPageContext({ page: "news", route: "/news", title: "今日资讯", view: showBriefing ? "briefing" : "feed", selection: { topic, search }, instanceId: "news-page" }); }, [search, setPageContext, showBriefing, topic]);

  const feed = useInfiniteQuery({
    queryKey: ["news-feed", topic],
    enabled: topic !== "收藏",
    initialPageParam: "",
    queryFn: ({ pageParam }) => api<NewsFeed>(`/api/news?topic=${encodeURIComponent(topic)}&limit=12${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`),
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined
  });
  const saved = useQuery({ queryKey: ["saved-news"], queryFn: () => api<NewsItem[]>("/api/news/saved") });
  const briefingQuery = useQuery({ queryKey: ["news-briefing-today"], queryFn: () => api<{ briefing: Briefing | null }>("/api/news/briefing/today") });

  const firstPage = feed.data?.pages[0];
  const isRefreshing = Boolean(firstPage?.refreshing);
  const refetchFeed = feed.refetch;
  useEffect(() => {
    if (!isRefreshing || topic === "收藏") return;
    const timer = window.setInterval(() => { void refetchFeed(); }, 1_800);
    return () => window.clearInterval(timer);
  }, [isRefreshing, topic, refetchFeed]);

  useEffect(() => { setSearch(""); }, [topic]);

  const save = useMutation({
    mutationFn: (articleId: string) => api<{ id: string }>("/api/news/save", { method: "POST", body: JSON.stringify({ articleId }) }),
    onSuccess: () => {
      setToast({ message: "已放进收藏，稍后可以慢慢读" });
      void qc.invalidateQueries({ queryKey: ["saved-news"] });
      void qc.invalidateQueries({ queryKey: ["news-feed"] });
    },
    onError: (error) => setToast({ message: errorMessage(error), type: "error" })
  });
  const removeSaved = useMutation({
    mutationFn: (savedId: string) => api(`/api/news/saved/${savedId}`, { method: "DELETE" }),
    onSuccess: () => {
      setToast({ message: "已从收藏移除" });
      void qc.invalidateQueries({ queryKey: ["saved-news"] });
      void qc.invalidateQueries({ queryKey: ["news-feed"] });
    },
    onError: (error) => setToast({ message: errorMessage(error), type: "error" })
  });
  const refresh = useMutation({
    mutationFn: () => api("/api/news/refresh", { method: "POST", body: JSON.stringify({ topic: topic === "收藏" ? "推荐" : topic }) }),
    onSuccess: () => {
      setToast({ message: "正在后台逐个更新新闻源，你可以继续阅读" });
      if (topic !== "收藏") void feed.refetch();
    },
    onError: (error) => setToast({ message: errorMessage(error), type: "error" })
  });
  const generateBriefing = useMutation({
    mutationFn: () => api<{ briefing: Briefing }>("/api/news/briefing", { method: "POST", body: "{}" }),
    onSuccess: (data) => {
      qc.setQueryData(["news-briefing-today"], { briefing: data.briefing });
      setShowBriefing(true);
    },
    onError: (error) => setToast({ message: errorMessage(error), type: "error" })
  });
  const regenerateBriefing = useMutation({
    mutationFn: () => api<{ briefing: Briefing }>("/api/news/briefing/regenerate", { method: "POST", body: "{}" }),
    onSuccess: (data) => {
      qc.setQueryData(["news-briefing-today"], { briefing: data.briefing });
      setConfirmRegenerate(false);
      setShowBriefing(true);
      setToast({ message: "今日晨报已按最新资讯重新生成" });
    },
    onError: (error) => setToast({ message: errorMessage(error), type: "error" })
  });

  const feedItems = useMemo(() => uniqueItems(feed.data?.pages.flatMap((page) => page.items) || []), [feed.data?.pages]);
  const rawItems = useMemo(() => topic === "收藏" ? (saved.data || []) : feedItems, [topic, saved.data, feedItems]);
  const normalizedSearch = search.trim().toLocaleLowerCase("zh-CN");
  const items = useMemo(() => normalizedSearch ? rawItems.filter((item) => `${item.title} ${item.summary} ${item.source}`.toLocaleLowerCase("zh-CN").includes(normalizedSearch)) : rawItems, [rawItems, normalizedSearch]);
  const savedIds = new Set((saved.data || []).map((item) => item.id));
  const categories = (firstPage?.categories || defaultCategories).map((category) => category.key === "收藏" ? { ...category, count: saved.data?.length || category.count } : category);
  const goodSources = firstPage?.sources.filter((source) => source.state === "ok").length || 0;
  const totalSources = firstPage?.sources.length || 0;
  const briefing = briefingQuery.data?.briefing || null;
  const leadItems = search ? [] : items.slice(0, 4);
  const listItems = search ? items : items.slice(4);

  function openBriefing() {
    if (briefing) setShowBriefing((value) => !value);
    else generateBriefing.mutate();
  }

  return <div className="news-intel">
    {toast && <Toast message={toast.message} type={toast.type} />}
    <section className="news-intel-status">
      <div>
        <span className="news-eyebrow"><Wifi size={13} /> PERSONAL INTELLIGENCE</span>
        <h2>今天值得你知道的事</h2>
        <p>{firstPage?.updatedAt ? `更新于 ${relativeTime(firstPage.updatedAt)}` : "正在建立你的私人资讯缓存"}{totalSources ? ` · ${goodSources}/${totalSources} 个来源可用` : ""}</p>
      </div>
      <div className="news-head-actions">
        <Button className="secondary small" aria-label={isRefreshing ? "资讯更新中" : "刷新资讯"} onClick={() => refresh.mutate()} loading={refresh.isPending} disabled={isRefreshing}><RefreshCw className={isRefreshing ? "spin" : ""} size={15} />{isRefreshing ? "更新中" : "刷新"}</Button>
        <Button className="small" onClick={openBriefing} loading={generateBriefing.isPending || briefingQuery.isLoading}><Sparkles size={15} />{briefing ? "今日 AI 晨报" : "生成 AI 晨报"}</Button>
      </div>
    </section>

    {showBriefing && briefing && <BriefingCard
      data={briefing}
      confirmRegenerate={confirmRegenerate}
      regenerating={regenerateBriefing.isPending}
      onClose={() => { setShowBriefing(false); setConfirmRegenerate(false); }}
      onAskRegenerate={() => setConfirmRegenerate(true)}
      onCancelRegenerate={() => setConfirmRegenerate(false)}
      onRegenerate={() => regenerateBriefing.mutate()}
    />}

    <section className="news-intel-controls" aria-label="资讯筛选与搜索">
      <div className="news-topic-sticky">
        <div className="news-topic-tabs" role="tablist" aria-label="资讯栏目">
          {categories.map((category) => <button
            key={category.key}
            type="button"
            role="tab"
            aria-selected={topic === category.key}
            className={topic === category.key ? "active" : ""}
            onClick={() => setTopic(category.key)}
          ><span>{category.label}</span>{category.count > 0 && <small>{category.count}</small>}</button>)}
        </div>
      </div>
      <label className="news-search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索已加载的标题、摘要或来源" /><span>{search && `${items.length} 条`}</span></label>
    </section>

    {firstPage?.sources.length ? <details className="news-source-health">
      <summary><CheckCircle2 size={14} />来源状态：{goodSources} 个正常{firstPage.partial ? "，部分来源暂不可用" : ""}<span>查看详情</span></summary>
      <div>{firstPage.sources.map((source) => <article key={source.id} className={source.state}>
        <i />
        <div><b>{source.name}</b><small>{source.topic} · {source.state === "ok" ? `${source.itemCount} 条` : source.state === "backoff" ? "暂时跳过" : "获取失败"}</small></div>
        <time>{source.lastSuccessAt ? `上次成功 ${relativeTime(source.lastSuccessAt)}` : "尚无成功记录"}</time>
      </article>)}</div>
    </details> : null}

    {(topic === "收藏" ? saved.isLoading : feed.isLoading) && <NewsSkeleton />}
    {(topic === "收藏" ? saved.isError : feed.isError) && <Card className="news-error"><Empty icon="📡" title="资讯暂时没有连接成功" description="上次内容仍会保留。你可以稍后重试，不会影响工作台其他功能。" /><Button className="secondary small" onClick={() => topic === "收藏" ? saved.refetch() : feed.refetch()}>重新连接</Button></Card>}

    {!search && leadItems.length > 0 && <section className="news-lead" aria-label="今日必读">
      <div className="news-section-heading"><div><span>EDITOR'S PICK</span><h2>今日必读</h2></div><p>新鲜度、来源质量与关注主题综合排序</p></div>
      <div className="news-lead-grid">
        <NewsCard item={leadItems[0]} variant="hero" saved={savedIds.has(leadItems[0].id)} onSave={() => save.mutate(leadItems[0].id)} onRemove={leadItems[0].savedId ? () => removeSaved.mutate(leadItems[0].savedId!) : undefined} />
        <div className="news-lead-stack">{leadItems.slice(1).map((item) => <NewsCard key={item.id} item={item} variant="compact" saved={savedIds.has(item.id)} onSave={() => save.mutate(item.id)} onRemove={item.savedId ? () => removeSaved.mutate(item.savedId!) : undefined} />)}</div>
      </div>
    </section>}

    {listItems.length > 0 && <section className="news-stream" aria-label={search ? "搜索结果" : "更多资讯"}>
      <div className="news-section-heading"><div><span>{search ? "SEARCH RESULTS" : topic === "收藏" ? "SAVED FOR LATER" : "LATEST STORIES"}</span><h2>{search ? `找到 ${items.length} 条` : topic === "收藏" ? "我的收藏" : "继续阅读"}</h2></div>{!search && topic !== "收藏" && <p>每次展示 12 条，减少无效信息</p>}</div>
      <div className="news-card-grid">{listItems.map((item) => <NewsCard key={`${topic}-${item.id}`} item={item} saved={savedIds.has(item.id)} onSave={() => save.mutate(item.id)} onRemove={item.savedId ? () => removeSaved.mutate(item.savedId!) : undefined} />)}</div>
    </section>}

    {!(topic === "收藏" ? saved.isLoading : feed.isLoading) && !items.length && <Card className="news-empty"><Empty icon={topic === "收藏" ? "🔖" : search ? "🔎" : "🗞️"} title={topic === "收藏" ? "收藏夹还是空的" : search ? "没有匹配的资讯" : "这个栏目暂时没有合适内容"} description={topic === "收藏" ? "遇到值得慢慢读的文章，点击书签放到这里。" : search ? "换一个更短的关键词试试。" : "系统会保留上次成功内容，并在后台继续尝试可靠来源。"} /></Card>}

    {!search && topic !== "收藏" && feed.hasNextPage && <div className="news-load-more"><Button className="secondary" loading={feed.isFetchingNextPage} onClick={() => feed.fetchNextPage()}><BookOpenText size={16} />加载更多</Button></div>}
    <p className="news-intel-disclaimer">资讯来自公开 RSS 与新闻聚合源，版权归原媒体所有。标题和摘要用于索引，重要信息请打开原文核实。</p>
  </div>;
}

function NewsArt({ item, large = false }: { item: NewsItem; large?: boolean }) {
  const [failed, setFailed] = useState(false);
  return <div className={`news-art topic-${item.topic.replaceAll(" ", "-")}${large ? " large" : ""}`}>
    {item.imageUrl && !failed ? <img src={item.imageUrl} alt="" loading={large ? "eager" : "lazy"} onError={() => setFailed(true)} /> : <div className="news-art-fallback"><Newspaper /><span>{item.topic}</span><small>PRIVE NEWSROOM</small></div>}
    {failed && <span className="news-image-missing"><ImageOff size={13} />来源图片未载入</span>}
  </div>;
}

function NewsCard({ item, variant = "standard", saved, onSave, onRemove }: { item: NewsItem; variant?: "standard" | "hero" | "compact"; saved: boolean; onSave: () => void; onRemove?: () => void }) {
  return <article className={`intel-news-card ${variant}`}>
    <NewsArt item={item} large={variant === "hero"} />
    <div className="intel-news-copy">
      <div className="intel-news-meta"><span>{item.topic}</span><i /> <b>{item.source}</b><time><Clock3 size={11} />{relativeTime(item.publishedAt)}</time></div>
      <h3><a href={item.url} target="_blank" rel="noopener noreferrer">{item.title}</a></h3>
      <p>{item.summary || "打开原文查看完整报道。"}</p>
      <div className="intel-news-actions"><a href={item.url} target="_blank" rel="noopener noreferrer">阅读原文 <ExternalLink size={13} /></a><button type="button" className={saved ? "saved" : ""} aria-label={onRemove ? "取消收藏" : saved ? "已收藏" : "收藏这篇资讯"} disabled={saved && !onRemove} onClick={onRemove || onSave}>{saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}</button></div>
    </div>
  </article>;
}

function NewsSkeleton() {
  return <div className="news-intel-skeleton" aria-label="正在读取资讯"><div className="skeleton hero" /><div><div className="skeleton" /><div className="skeleton" /><div className="skeleton" /></div></div>;
}

function BriefingCard({ data, confirmRegenerate, regenerating, onClose, onAskRegenerate, onCancelRegenerate, onRegenerate }: {
  data: Briefing;
  confirmRegenerate: boolean;
  regenerating: boolean;
  onClose: () => void;
  onAskRegenerate: () => void;
  onCancelRegenerate: () => void;
  onRegenerate: () => void;
}) {
  return <Card className="intel-briefing">
    <div className="intel-briefing-head"><div><span><Sparkles size={14} /> DAILY AI BRIEF</span><small>{new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric" }).format(new Date(data.generatedAt))} · 当日已缓存</small></div><button type="button" onClick={onClose} aria-label="关闭晨报"><X size={18} /></button></div>
    <h2>{data.headline}</h2><p className="intel-briefing-overview">{data.overview}</p>
    <div className="intel-briefing-sections">{data.sections.map((section) => <section key={section.title}><h3>{section.title}</h3>{section.points.map((point, index) => <div className="intel-briefing-point" key={`${section.title}-${index}`}><p>{point.text}</p>{point.sources.length > 0 && <div>{point.sources.map((source) => <a key={source.id} href={source.url} target="_blank" rel="noopener noreferrer">{source.title}<ExternalLink size={10} /></a>)}</div>}</div>)}</section>)}</div>
    <div className="intel-briefing-bottom"><div className="intel-english"><span>ENGLISH CORNER</span><b>{data.englishCorner.term}</b><p>{data.englishCorner.meaning}</p><q>{data.englishCorner.example}</q></div><blockquote>{data.reflectionQuestion}</blockquote></div>
    <div className="intel-briefing-foot"><small>{data.disclaimer}</small>{confirmRegenerate ? <div className="briefing-confirm"><span>重新生成会再次调用 Kimi 并产生费用。</span><button onClick={onCancelRegenerate}>取消</button><button className="danger" disabled={regenerating} onClick={onRegenerate}>{regenerating ? "生成中…" : "确认重新生成"}</button></div> : <button type="button" onClick={onAskRegenerate}><RefreshCw size={13} />按最新资讯重新生成</button>}</div>
  </Card>;
}
